import React from 'react';
import { motion } from 'framer-motion';
import { LogIn, MessageSquareDashed, UserPlus } from 'lucide-react';
export function SlideJourney() {
  // Retention through the Snak™ experience, step by step (from Snak Journey report)
  const steps = [{
    label: "Let's go",
    retention: 100,
    drop: 0
  }, {
    label: 'Unlock reward',
    retention: 100,
    drop: 24
  }, {
    label: 'Where you fit',
    retention: 76,
    drop: 8
  }, {
    label: 'Biggest bottleneck',
    retention: 68,
    drop: 5
  }, {
    label: 'Minerva reputation',
    retention: 63,
    drop: 2
  }, {
    label: 'CRO/CDMO factors',
    retention: 61,
    drop: 2
  }, {
    label: 'Minerva reputation',
    retention: 59,
    drop: 0
  }, {
    label: 'Future capability',
    retention: 59,
    drop: 0
  }, {
    label: 'Minerva reputation',
    retention: 59,
    drop: 0
  }, {
    label: 'Enter details',
    retention: 59,
    drop: 30
  }, {
    label: 'Thank you',
    retention: 29,
    drop: 0
  }];
  // Chart geometry
  const W = 1000;
  const H = 240;
  const padX = 36;
  const padY = 24;
  const innerW = W - padX * 2;
  const innerH = H - padY * 2;
  const x = (i: number) => padX + i / (steps.length - 1) * innerW;
  const y = (r: number) => padY + (1 - r / 100) * innerH;
  const linePath = steps.map((s, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(s.retention)}`).join(' ');
  const areaPath = `${linePath} L ${x(steps.length - 1)} ${padY + innerH} L ${x(0)} ${padY + innerH} Z`;
  const dropColor = (d: number) => d === 0 ? {
    dot: '#10B981',
    text: 'text-emerald-600',
    bg: 'bg-emerald-50'
  } : d >= 20 ? {
    dot: '#E11D48',
    text: 'text-rose-600',
    bg: 'bg-rose-50'
  } : {
    dot: '#F59E0B',
    text: 'text-amber-600',
    bg: 'bg-amber-50'
  };
  const insights = [{
    icon: LogIn,
    title: '−24% at the commitment gate',
    desc: 'The first real ask — “answer a few questions to unlock your reward” — filtered out casual scanners early. Those who stayed were genuinely interested.',
    tone: 'amber'
  }, {
    icon: MessageSquareDashed,
    title: 'Near-zero drop through the questions',
    desc: 'From 76% down to 59%, the strategic questions held attention — all three reputation ratings lost nobody. People who started the Pulse finished it.',
    tone: 'emerald'
  }, {
    icon: UserPlus,
    title: '−30% at contact capture — expected',
    desc: 'The largest single drop is where people are asked for their details. Friction here is normal and healthy; it still produced 12 qualified leads.',
    tone: 'rose'
  }];
  const toneMap: Record<string, string> = {
    amber: 'bg-amber-50 border-amber-100 text-amber-600',
    emerald: 'bg-emerald-50 border-emerald-100 text-emerald-600',
    rose: 'bg-rose-50 border-rose-100 text-rose-600'
  };
  return <div className="flex flex-col h-full max-w-[1600px] mx-auto w-full" data-id="element-757">
      <div className="mb-5" data-id="element-758">
        <h2 className="text-4xl font-display font-bold text-slate-900 mb-3" data-id="element-759">
          Where the audience fell off
        </h2>
        <p className="text-lg text-slate-500 max-w-4xl" data-id="element-760">
          Tracing the Snak™ journey step by step. Almost all drop-off happened
          at just two moments — the early commitment gate and the final contact
          form — while the strategic questions in between held attention.
        </p>
      </div>

      {/* Retention chart */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5" data-id="element-761">
        <svg viewBox={`0 0 ${W} ${H + 56}`} className="w-full" data-id="element-762">
          {/* gridlines */}
          {[0, 25, 50, 75, 100].map(g => <g key={g} data-id="element-763">
              <line x1={padX} x2={W - padX} y1={y(g)} y2={y(g)} stroke="#F1F5F9" strokeWidth="1" data-id="element-764" />
              <text x={padX - 8} y={y(g) + 3} textAnchor="end" fontSize="9" className="fill-slate-300" data-id="element-765">
                {g}%
              </text>
            </g>)}

          {/* area */}
          <motion.path d={areaPath} fill="#0085bd" fillOpacity="0.08" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.6
        }} data-id="element-766" />
          {/* line */}
          <motion.path d={linePath} fill="none" stroke="#0085bd" strokeWidth="2.5" strokeLinejoin="round" initial={{
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          duration: 1.4,
          ease: 'easeInOut'
        }} data-id="element-767" />

          {/* points + labels */}
          {steps.map((s, i) => {
          const c = dropColor(s.drop);
          return <motion.g key={i} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.4 + i * 0.1
          }} data-id="element-768">
                <circle cx={x(i)} cy={y(s.retention)} r="4.5" fill="#fff" stroke={c.dot} strokeWidth="2.5" data-id="element-769" />
                {/* retention % above point */}
                <text x={x(i)} y={y(s.retention) - 10} textAnchor="middle" fontSize="11" fontWeight="700" className="fill-slate-700" data-id="element-770">
                  {s.retention}%
                </text>
                {/* drop badge below for non-zero drops */}
                {s.drop > 0 && <text x={x(i)} y={y(s.retention) + 18} textAnchor="middle" fontSize="10" fontWeight="700" fill={c.dot} data-id="element-771">
                    −{s.drop}%
                  </text>}
                {/* step label rotated under axis */}
                <text x={x(i)} y={H + 14} textAnchor="end" fontSize="9.5" className="fill-slate-400" transform={`rotate(-35 ${x(i)} ${H + 14})`} data-id="element-772">
                  {i + 1}. {s.label}
                </text>
              </motion.g>;
        })}
        </svg>
      </div>

      {/* Insight cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5" data-id="element-773">
        {insights.map((ins, i) => {
        const Icon = ins.icon;
        return <motion.div key={i} initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.9 + i * 0.12
        }} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex flex-col" data-id="element-774">
              <div className={`w-10 h-10 rounded-xl border flex items-center justify-center mb-3 ${toneMap[ins.tone]}`} data-id="element-775">
                <Icon className="w-5 h-5" data-id="element-776" />
              </div>
              <h3 className="text-base font-bold text-slate-800 mb-1.5" data-id="element-777">
                {ins.title}
              </h3>
              <p className="text-sm text-slate-500 leading-relaxed" data-id="element-778">
                {ins.desc}
              </p>
            </motion.div>;
      })}
      </div>
    </div>;
}